// Sea, lagoon and shoreline shading for the Earth terrain patch.
//
// Water is not a separate mesh: the terrain sits at 0 m over water and the
// terrain shader decides, per pixel, from the coastline distance fields
// (coast.js), whether it is looking at land or water. Water pixels then go
// through three.js's own PBR lighting with the terrain's normal, roughness and
// F0 swapped for water's, so the sky reflection, the sun glint and shadows all
// come from the real lights and environment map.
//
// Waves are a sum of directional deep-water waves with analytic slopes. Each
// wave only perturbs the normal where the pixel can resolve it (a few pixels
// per wavelength); the slope variance of the ones it cannot resolve is added
// to the roughness instead, which is what turns sparkling ripples at the pad
// into a broad glitter path at the horizon and a soft glint from orbit (the
// Cox-Munk sun-glitter model, about 0.028 total slope variance for a 5 m/s
// wind). Phases are computed on the CPU per frame about an origin under the
// camera, so a 0.4 m ripple keeps its precision 50 km from the pad.

import * as THREE from 'three';

// Deep-water waves: direction of travel (rad; 0 = east, pi/2 = south),
// wavelength (m), steepness a*k, phase. The swell arrives from the east-north-
// east, the wind sea from the east-south-east, as it usually does on the Cape.
export const WAVES = [
  [2.92, 71.0, 0.050, 0.0],
  [3.22, 44.0, 0.046, 1.7],
  [2.70, 27.5, 0.052, 4.1],
  [3.45, 16.3, 0.058, 2.3],
  [2.98, 10.1, 0.064, 5.2],
  [2.52, 6.3, 0.068, 0.9],
  [3.62, 3.95, 0.070, 3.3],
  [2.84, 2.47, 0.070, 1.1],
  [3.30, 1.55, 0.066, 4.7],
  [2.60, 0.97, 0.060, 2.9],
  [3.90, 0.61, 0.055, 0.4],
  [2.36, 0.38, 0.050, 5.9]
];
const G = 9.81;

export const waterUniforms = {
  uWaterTime: { value: 0 },
  uWaveOrigin: { value: new THREE.Vector2() },
  uWavePhase: { value: new Float32Array(WAVES.length) }
};

// Shared with the near-ground sea (sea.js), which fades in over this water
// below a couple of kilometres: how much of a pixel that layer owns, where
// the camera is in the patch frame, how far its mesh reaches, and the clock
// its shore train runs on. Everything here is zero while the sea layer is
// not up, so the water from altitude is exactly what it always was.
export const seaUniforms = {
  uSeaCover: { value: 0 },
  uSeaCam: { value: new THREE.Vector2() },
  uSeaRange: { value: 1 },
  uSeaSwash: { value: new THREE.Vector4(9, 0.62, 0, 0) },  // period (s), run-up (m), clock, on
  uSeaPlane: { value: new THREE.Vector2(0, 6371000) }      // ground height at the patch centre, body radius
};

// Per frame: origin (patch-local x, z of the camera) and the time, in double
// precision, folded into one phase offset per wave.
export function updateWaves(time, originX, originZ) {
  waterUniforms.uWaterTime.value = time;
  waterUniforms.uWaveOrigin.value.set(originX, originZ);
  const ph = waterUniforms.uWavePhase.value;
  for (let i = 0; i < WAVES.length; i++) {
    const [a, lambda, , phi] = WAVES[i];
    const k = 2 * Math.PI / lambda;
    const w = Math.sqrt(G * k);
    const p = k * (Math.cos(a) * originX + Math.sin(a) * originZ) - w * time + phi;
    ph[i] = p - Math.floor(p / (2 * Math.PI)) * 2 * Math.PI;
  }
}

// The along-shore scatter the shore train rides, shared verbatim with
// sea.js: a stretch of bank has to see the same crest arrive in both
// shaders or the sand would dry out while a wave is still running up it.
export const SEA_SCATTER_GLSL = /* glsl */`
float seaScatterHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}
float seaScatterValue(vec2 p) {
  vec2 i = floor(p), a = fract(p);
  vec2 u = a * a * (3.0 - 2.0 * a);
  return mix(mix(seaScatterHash(i), seaScatterHash(i + vec2(1.0, 0.0)), u.x),
             mix(seaScatterHash(i + vec2(0.0, 1.0)), seaScatterHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float seaScatterNoise(vec2 p) {
  return seaScatterValue(p * 0.0055) * 0.72 + seaScatterValue(p * 0.017 + 11.0) * 0.30;
}
`;

const f = (x) => x.toFixed(5);
const WAVE_TABLE = WAVES.map(([a, l, s]) => {
  const k = 2 * Math.PI / l;
  return `vec4(${f(Math.cos(a) * k)}, ${f(Math.sin(a) * k)}, ${f(l)}, ${f(s)})`;
}).join(',\n  ');

// Declarations and helpers for the terrain fragment shader.
export const WATER_PARS = /* glsl */`
uniform float uWaterTime;
uniform vec2 uWaveOrigin;
uniform float uWavePhase[${WAVES.length}];
// Direction times wavenumber (x, z), wavelength (m), steepness.
const vec4 WAVE_K[${WAVES.length}] = vec4[${WAVES.length}](
  ${WAVE_TABLE}
);

// Linear sRGB <-> Oklab, for tint blends that stay clean instead of dipping
// through grey the way a raw RGB mix of blue and green does.
vec3 wLinToOklab(vec3 c) {
  vec3 l = vec3(0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b,
                0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b,
                0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b);
  l = pow(max(l, vec3(0.0)), vec3(1.0 / 3.0));
  return vec3(0.2104542553 * l.x + 0.7936177850 * l.y - 0.0040720468 * l.z,
              1.9779984951 * l.x - 2.4285922050 * l.y + 0.4505937099 * l.z,
              0.0259040371 * l.x + 0.7827717662 * l.y - 0.8086757660 * l.z);
}
vec3 wOklabToLin(vec3 c) {
  vec3 l = vec3(c.x + 0.3963377774 * c.y + 0.2158037573 * c.z,
                c.x - 0.1055613458 * c.y - 0.0638541728 * c.z,
                c.x - 0.0894841775 * c.y - 1.2914855480 * c.z);
  l = l * l * l;
  return vec3(4.0767416621 * l.x - 3.3077115913 * l.y + 0.2309699292 * l.z,
             -1.2684380046 * l.x + 2.6097574011 * l.y - 0.3413193965 * l.z,
             -0.0041960863 * l.x - 0.7034186147 * l.y + 1.7076147010 * l.z);
}
vec3 wMix(vec3 a, vec3 b, float t) { return wOklabToLin(mix(wLinToOklab(a), wLinToOklab(b), t)); }

// Wave slopes (dh/dx, dh/dz) at patch-local p for a pixel footprint fp (m).
// calm scales the sea down to a fetch-limited lagoon chop. Returns the slope
// variance of the waves the pixel cannot resolve in varOut.
vec2 waveSlope(vec2 p, float fp, float calm, out float varOut) {
  vec2 q = p - uWaveOrigin;
  vec2 s = vec2(0.0);
  float v = 0.0;
  for (int i = 0; i < ${WAVES.length}; i++) {
    vec4 w = WAVE_K[i];
    // Lagoons only raise short chop, and less of it.
    float amp = w.w * mix(1.0, 0.55 * (1.0 - smoothstep(1.8, 5.0, w.z)), calm);
    float res = clamp(w.z / fp * 0.25 - 0.5, 0.0, 1.0);
    float ph = dot(w.xy, q) + uWavePhase[i];
    // Slightly peaked crests: steeper fronts read as wind waves, not ripples.
    float c = cos(ph);
    c = c * (1.0 + 0.35 * sin(ph));
    s += amp * res * c * normalize(w.xy);
    v += (1.0 - res) * amp * amp * 0.5;
  }
  varOut = v;
  return s;
}

float wHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float wNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(wHash(i), wHash(i + vec2(1.0, 0.0)), f.x), mix(wHash(i + vec2(0.0, 1.0)), wHash(i + vec2(1.0, 1.0)), f.x), f.y);
}

uniform float uSeaCover;
uniform vec2 uSeaCam;
uniform float uSeaRange;
uniform vec4 uSeaSwash;
uniform vec2 uSeaPlane;
${SEA_SCATTER_GLSL}
// How much of this water the near-ground sea layer owns, so the beach and
// the bed are shaded once, by whichever of the two is drawing them.
float seaCoverAt(vec2 p) {
  return uSeaCover * (1.0 - smoothstep(0.72 * uSeaRange, uSeaRange, length(p - uSeaCam)));
}
// Height of a patch-local point above the sea-level sphere, in metres. The
// sea layer's surface lies on that sphere, so where this is positive the
// ground drawn here is in front of it and the sea cannot be seen at all:
// a ring whose cells are wider than the shoreline it crosses interpolates
// land out over the water like that, and the water there stays this
// shader's to draw.
float seaAboveLevel(vec3 local) {
  return local.y + uSeaPlane.x + dot(local.xz, local.xz) / (2.0 * uSeaPlane.y);
}
// How far up the beach the swash has run, in metres inland from the still
// waterline: a fast uprush as each bore arrives and a long drain after it,
// on the sea's own shore-train clock and its along-shore scatter, so the
// sand darkens exactly when a wave breaks in front of it.
float seaSwashRun(vec2 p, float slope) {
  float ph = fract(uSeaSwash.z / max(uSeaSwash.x, 0.5) + seaScatterNoise(p) * 0.6);
  const float rise = 0.3;
  float s = ph < rise ? ph / rise : 1.0 - (ph - rise) / (1.0 - rise);
  return uSeaSwash.y / max(slope, 0.02) * smoothstep(0.0, 1.0, s);
}
`;

// Linear-space body colours. The offshore navy is the Blue Marble ocean over
// the Florida shelf, so the sea seen from altitude agrees with the globe.
// sea.js reads the same numbers, so the near-ground water crosses over into
// this one without a step in colour.
export const WATER_TINTS = {
  oceanSurf: [0.052, 0.120, 0.100],
  oceanShelf: [0.020, 0.068, 0.072],
  oceanDeep: [0.012, 0.030, 0.050],
  lagoon: [0.034, 0.040, 0.026],
  foam: [0.80, 0.82, 0.80],
  sandDry: [0.50, 0.44, 0.33],
  sandWet: [0.24, 0.21, 0.16]
};
const tint = (name, key) => `const vec3 ${name} = vec3(${WATER_TINTS[key].join(', ')});`;
export const WATER_COLOURS = /* glsl */`
${tint('OCEAN_SURF', 'oceanSurf')}
${tint('OCEAN_SHELF', 'oceanShelf')}
${tint('OCEAN_DEEP', 'oceanDeep')}
${tint('LAGOON_MURK', 'lagoon')}
${tint('FOAM_COL', 'foam')}
${tint('SAND_DRY', 'sandDry')}
${tint('SAND_WET', 'sandWet')}
`;
